import { useSearchParams } from 'react-router-dom';

export type TriageDensity = 'comfortable' | 'dense';

const STORAGE_KEY = 'triageDensity';

function isDensity(value: string | null): value is TriageDensity {
  return value === 'comfortable' || value === 'dense';
}

/** Last density the user chose, so the preference survives a full page load. */
function readStoredDensity(): TriageDensity {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isDensity(stored) ? stored : 'comfortable';
  } catch {
    // Private-mode / disabled storage — fall back to the default.
    return 'comfortable';
  }
}

/**
 * URL-backed (`?density=`) row density for the Triage dashboard, mirroring
 * `useTriageView`. Unlike view, density is a durable per-user preference, so an
 * explicit choice is also mirrored to localStorage and used when the URL is silent.
 */
export function useTriageDensity() {
  const [searchParams, setSearchParams] = useSearchParams();

  const densityParam = searchParams.get('density');
  const density: TriageDensity = isDensity(densityParam) ? densityParam : readStoredDensity();

  const setDensity = (next: TriageDensity) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Preference just won't persist; the URL still drives this session.
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('density', next);
    setSearchParams(nextParams, { replace: true });
  };

  return { density, setDensity };
}
